import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

const UNITS = ["g", "kg", "ml", "L", "pcs", "bags", "bottles", "boxes", "cloves"];

function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [tab, setTab] = useState("recipes");

  const [newRecipeName, setNewRecipeName] = useState("");
  const [newPortions, setNewPortions] = useState(1);
  const [newMenuItemId, setNewMenuItemId] = useState("");
  const [recipeError, setRecipeError] = useState("");
  const [menu, setMenu] = useState([]);

  const [newIngName, setNewIngName] = useState("");
  const [newIngUnit, setNewIngUnit] = useState("kg");
  const [newIngQty, setNewIngQty] = useState(1);
  const [newIngPrice, setNewIngPrice] = useState("");
  const [ingError, setIngError] = useState("");
  const [editingIng, setEditingIng] = useState(null);

  const navigate = useNavigate();

  const fetchRecipes = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/recipes`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setRecipes(data);
    } catch (err) { console.error("Error fetching recipes:", err); }
  };

  const fetchIngredients = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/ingredients`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setIngredients(data);
    } catch (err) { console.error("Error fetching ingredients:", err); }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) setMenu(data);
    } catch (err) { console.error("Error fetching menu:", err); }
  };

  useEffect(() => { Promise.allSettled([fetchRecipes(), fetchIngredients(), fetchMenu()]); }, []);

  const createRecipe = async (e) => {
    e.preventDefault();
    setRecipeError("");
    if (!newRecipeName.trim()) return setRecipeError("Recipe name is required.");
    if (newPortions <= 0) return setRecipeError("Portions must be at least 1.");

    const res = await fetch(`${API_URL}/api/admin/recipes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: newRecipeName, portions: newPortions, menu_item_id: newMenuItemId || null }),
    });
    const data = await res.json();
    if (!res.ok) return setRecipeError(data.error);
    setNewRecipeName(""); setNewPortions(1); setNewMenuItemId("");
    fetchRecipes();
  };

  const deleteRecipe = async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    await fetch(`${API_URL}/api/admin/recipes/${id}`, { method: "DELETE", credentials: "include" });
    fetchRecipes();
  };

  const createIngredient = async (e) => {
    e.preventDefault();
    setIngError("");
    if (!newIngName.trim()) return setIngError("Name is required.");
    if (!newIngPrice) return setIngError("Price is required.");

    const res = await fetch(`${API_URL}/api/admin/ingredients`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: newIngName,
        unit: newIngUnit,
        purchase_quantity: parseFloat(newIngQty) || 1,
        price_per_unit: parseFloat(newIngPrice),
      }),
    });
    const data = await res.json();
    if (!res.ok) return setIngError(data.error);
    setNewIngName(""); setNewIngUnit("kg"); setNewIngQty(1); setNewIngPrice("");
    fetchIngredients();
  };

  const saveIngredientEdit = async (ing) => {
    await fetch(`${API_URL}/api/admin/ingredients/${ing.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(ing),
    });
    setEditingIng(null);
    fetchIngredients();
  };

  const deleteIngredient = async (id) => {
    if (!window.confirm("Delete this ingredient?")) return;
    await fetch(`${API_URL}/api/admin/ingredients/${id}`, { method: "DELETE", credentials: "include" });
    fetchIngredients();
  };

  const costPerUnit = (ing) =>
    (parseFloat(ing.price_per_unit) / parseFloat(ing.purchase_quantity)).toFixed(4);

  return (
    <div style={{ maxWidth: "800px" }}>
      <h1 className="admin-page-heading">🍳 Recipes & Ingredients</h1>
      <p className="admin-page-sub">Manage your recipes and shopping list</p>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        <button onClick={() => setTab("recipes")} style={tab === "recipes" ? activeTabStyle : tabStyle}>
          📖 Recipes
        </button>
        <button onClick={() => setTab("ingredients")} style={tab === "ingredients" ? activeTabStyle : tabStyle}>
          🛒 Shopping List
        </button>
      </div>

      {tab === "recipes" && (
        <>
          <div style={cardStyle}>
            <h2 style={sectionHeading}>New Recipe</h2>
            <form onSubmit={createRecipe} style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: 2, minWidth: "180px" }}>
                <label style={labelStyle}>Recipe Name</label>
                <input value={newRecipeName}
                  onChange={e => setNewRecipeName(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: "160px" }}>
                <label style={labelStyle}>Links to Menu Item</label>
                <select value={newMenuItemId} onChange={e => setNewMenuItemId(e.target.value)} style={inputStyle}>
                  <option value="">Not linked yet</option>
                  {menu.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: "120px" }}>
                <label style={labelStyle}>Portions</label>
                <input type="number" min="1" value={newPortions}
                  onChange={e => setNewPortions(Number(e.target.value))} style={inputStyle} />
              </div>
              <button type="submit" style={createBtnStyle}>+ Create</button>
            </form>
            {recipeError && <p style={errorStyle}>{recipeError}</p>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1rem" }}>
            {recipes.length === 0
              ? <p style={{ color: "var(--text-muted)" }}>No recipes yet.</p>
              : recipes.map(r => (
                <div key={r.id} style={recipeCardStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong style={{ fontSize: "1.05rem", color: "var(--text-main)" }}>{r.name}</strong>
                      <span style={{ marginLeft: "0.75rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                        {r.portions} portion{r.portions > 1 ? "s" : ""}
                      </span>
                      {r.menu_item_id ? (
                        <span style={{ marginLeft: "0.75rem", fontSize: "0.82rem", background: "#EDF5EE", color: "var(--success)", padding: "0.15rem 0.5rem", borderRadius: "999px" }}>
                          🔗 {menu.find(m => m.id === r.menu_item_id)?.name || "Linked"}
                        </span>
                      ) : (
                        <span style={{ marginLeft: "0.75rem", fontSize: "0.82rem", background: "#FEF4E4", color: "var(--orange)", padding: "0.15rem 0.5rem", borderRadius: "999px" }}>
                          ⚠️ Not linked
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button onClick={() => navigate(`/admin/recipes/${r.id}`)} style={editBtnStyle}>
                        ✏️ Edit Ingredients
                      </button>
                      <button onClick={() => deleteRecipe(r.id)} style={deleteBtnStyle}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </>
      )}

      {tab === "ingredients" && (
        <>
          <div style={cardStyle}>
            <h2 style={sectionHeading}>Add to Shopping List</h2>
            <p style={{ margin: "0 0 1rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
              Just enter what you buy — the system handles all measurements automatically.
            </p>

            <form onSubmit={createIngredient}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "0.75rem" }}>
                <div>
                  <label style={labelStyle}>Ingredient Name</label>
                  <input value={newIngName}
                    onChange={e => setNewIngName(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Package unit</label>
                  <select value={newIngUnit} onChange={e => setNewIngUnit(e.target.value)} style={inputStyle}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Package size</label>
                  <input type="number" min="0.001" step="0.001"
                    value={newIngQty} onChange={e => setNewIngQty(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Price per pkg (RM)</label>
                  <input type="number" min="0" step="0.01"
                    value={newIngPrice} onChange={e => setNewIngPrice(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {newIngPrice && newIngQty && newIngName && (
                <div style={previewStyle}>
                  📦 {newIngName}: {newIngQty} {newIngUnit} for RM {parseFloat(newIngPrice).toFixed(2)}
                  {" → "}
                  RM {(parseFloat(newIngPrice) / parseFloat(newIngQty)).toFixed(4)} per {newIngUnit}
                </div>
              )}

              {ingError && <p style={errorStyle}>{ingError}</p>}
              <button type="submit" style={createBtnStyle}>+ Add</button>
            </form>
          </div>

          <div style={{ marginTop: "1rem", overflowX: "auto" }}>
            {ingredients.length === 0
              ? <p style={{ color: "var(--text-muted)" }}>No ingredients yet.</p>
              : (
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                      <th style={thStyle}>Ingredient</th>
                      <th style={thStyle}>Package</th>
                      <th style={thStyle}>Price</th>
                      <th style={thStyle}>Cost per unit</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map(ing => (
                      <tr key={ing.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        {editingIng?.id === ing.id ? (
                          <>
                            <td style={tdStyle}>
                              <input value={editingIng.name}
                                onChange={e => setEditingIng({ ...editingIng, name: e.target.value })}
                                style={{ ...inputStyle, width: "120px" }} />
                            </td>
                            <td style={tdStyle} colSpan={1}>
                              <div style={{ display: "flex", gap: "0.4rem" }}>
                                <input type="number" value={editingIng.purchase_quantity}
                                  onChange={e => setEditingIng({ ...editingIng, purchase_quantity: e.target.value })}
                                  style={{ ...inputStyle, width: "65px" }} />
                                <select value={editingIng.unit}
                                  onChange={e => setEditingIng({ ...editingIng, unit: e.target.value })}
                                  style={{ ...inputStyle, width: "70px" }}>
                                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <input type="number" step="0.01" value={editingIng.price_per_unit}
                                onChange={e => setEditingIng({ ...editingIng, price_per_unit: e.target.value })}
                                style={{ ...inputStyle, width: "80px" }} />
                            </td>
                            <td style={tdStyle}>
                              RM {(parseFloat(editingIng.price_per_unit) / parseFloat(editingIng.purchase_quantity)).toFixed(4)}
                              /{editingIng.unit}
                            </td>
                            <td style={tdStyle}>
                              <button onClick={() => saveIngredientEdit(editingIng)} style={saveBtnStyle}>Save</button>
                              <button onClick={() => setEditingIng(null)} style={{ ...cancelBtnStyle, marginLeft: "0.4rem" }}>✕</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={tdStyle}><strong>{ing.name}</strong></td>
                            <td style={tdStyle}>{ing.purchase_quantity} {ing.unit}</td>
                            <td style={tdStyle}>RM {parseFloat(ing.price_per_unit).toFixed(2)}</td>
                            <td style={tdStyle}>RM {costPerUnit(ing)}/{ing.unit}</td>
                            <td style={tdStyle}>
                              <button onClick={() => setEditingIng({ ...ing })} style={editBtnStyle}>✏️</button>
                              <button onClick={() => deleteIngredient(ing.id)} style={{ ...deleteBtnStyle, marginLeft: "0.4rem" }}>🗑</button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </>
      )}
    </div>
  );
}

const sectionHeading = { marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" };
const cardStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)" };
const recipeCardStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "14px", padding: "1rem 1.25rem", boxShadow: "0 2px 12px rgba(92, 51, 23, 0.06)" };
const tableStyle = { width: "100%", borderCollapse: "collapse", background: "var(--warm-white)", borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(92, 51, 23, 0.06)" };
const labelStyle = { display: "block", fontWeight: "600", fontSize: "0.82rem", color: "var(--brown-mid)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" };
const inputStyle = { display: "block", width: "100%", padding: "0.6rem 0.85rem", marginBottom: "0", borderRadius: "10px", border: "1.5px solid var(--border)", background: "var(--beige)", fontSize: "0.95rem", boxSizing: "border-box", color: "var(--text-main)" };
const createBtnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.6rem 1.25rem", borderRadius: "10px", fontSize: "0.95rem", fontWeight: "700", cursor: "pointer", marginTop: "0.75rem", boxShadow: "0 4px 14px rgba(224, 123, 57, 0.35)" };
const saveBtnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.3rem 0.7rem", borderRadius: "8px", fontSize: "0.82rem", cursor: "pointer", fontWeight: "700" };
const cancelBtnStyle = { background: "transparent", border: "1px solid var(--border)", padding: "0.3rem 0.6rem", borderRadius: "8px", fontSize: "0.82rem", cursor: "pointer", color: "var(--text-muted)" };
const editBtnStyle = { background: "var(--beige)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.82rem", cursor: "pointer" };
const deleteBtnStyle = { background: "#FDECEC", border: "1px solid #E8A8A8", borderRadius: "8px", padding: "0.3rem 0.6rem", fontSize: "0.82rem", cursor: "pointer", color: "var(--error)" };
const tabStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "10px", padding: "0.5rem 1.25rem", fontSize: "0.95rem", cursor: "pointer", color: "var(--text-muted)" };
const activeTabStyle = { ...tabStyle, background: "var(--orange)", color: "white", border: "1px solid var(--orange)" };
const thStyle = { padding: "0.75rem 1rem", textAlign: "left", fontSize: "0.78rem", fontWeight: "700", color: "var(--brown-mid)", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle = { padding: "0.65rem 1rem", fontSize: "0.9rem", color: "var(--text-main)" };
const errorStyle = { color: "var(--error)", fontSize: "0.88rem", marginTop: "0.5rem" };
const previewStyle = { background: "#EDF5EE", border: "1px solid var(--success)", borderRadius: "10px", padding: "0.6rem 0.9rem", fontSize: "0.85rem", color: "var(--success)", margin: "0.75rem 0" };

export default Recipes;
